import { Separator, Text } from '@cuemath/web-comps';
import React from 'react';
import {
  Button,
  Loader,
  Modal,
  ModalActions,
  ModalContent,
  ModalDescription,
  ModalHeader,
  Segment,
} from 'semantic-ui-react';
import { Col, Row } from '@cuemath/comps';
import styled from 'styled-components';

import COLORS from '../../../constants/colors';
import { useGetEvalSpecialistSlots } from 'actions/eval-reporting';
import {
  CAPACITY_SLOT_STATUSES,
  formatDateTimeInZone,
  formatMonthLabel,
} from '../common/constants';
import { ErrorLine, MonoText } from '../common/filters';

const ContextCard = styled(Col)`
  background: ${COLORS.WILD_SAND};
  border-radius: 8px;
`;

const ContextRow = styled(Row)`
  padding: 12px 16px;
`;

const ContextRowTight = styled(Row)`
  padding: 0 16px 12px;
`;

const SlotRow = styled(Row)`
  padding: 6px 0;
  gap: 12px;
  align-items: center;
  border-bottom: 1px solid ${COLORS.GRAY_LIGHT};
`;

const SlotList = styled(Col)`
  max-height: 320px;
  overflow-y: auto;
`;

const SlotDetail = ({ target, closeModal }) => {
  const { row, status, month, zone } = target;
  const isFilled = status === CAPACITY_SLOT_STATUSES.filled;

  const {
    data,
    get: getSlots,
    isProcessing: isLoading,
    error: slotsError,
  } = useGetEvalSpecialistSlots();

  React.useEffect(() => {
    getSlots(row.teacher_id, { month, status });
  }, [getSlots, row.teacher_id, month, status]);

  const slots = data?.slots || [];

  const renderBody = () => {
    if (isLoading) return <Loader active inline="centered" />;
    if (slotsError) return <ErrorLine error={slotsError} />;

    if (!slots.length) {
      return (
        <Segment>
          <Text variant="body2" color={COLORS.GRAY}>
            No {status} slots to show for this month.
          </Text>
        </Segment>
      );
    }

    return (
      <SlotList gutter={0}>
        {slots.map(slot => (
          <SlotRow key={slot.slot_id || slot.assessment_id || slot.start}>
            <MonoText>{formatDateTimeInZone(slot.start, zone)}</MonoText>
            {isFilled && (
              <Text variant="body3" color={COLORS.GRAY}>
                {slot.student_name || 'Student not named'}
                {slot.state ? ` · ${slot.state}` : ''}
              </Text>
            )}
          </SlotRow>
        ))}
      </SlotList>
    );
  };

  return (
    <Modal open size="small" onClose={closeModal}>
      <ModalHeader>{isFilled ? 'Filled slots' : 'Available slots'}</ModalHeader>
      <ModalContent scrolling>
        <ModalDescription>
          <ContextCard gutter={0}>
            <ContextRow gutter={0}>
              <Text variant="body2">
                {row.teacher_name || 'Unnamed specialist'} · {formatMonthLabel(month)}
              </Text>
            </ContextRow>
            <ContextRowTight gutter={0}>
              <Text variant="body3" color={COLORS.GRAY}>
                {row.teacher_id}
              </Text>
            </ContextRowTight>
            <ContextRowTight gutter={0}>
              <Text variant="body3" color={COLORS.GRAY}>
                given {row.given ?? 0} · filled {row.filled ?? 0} · available{' '}
                {row.available ?? 0}
              </Text>
            </ContextRowTight>
          </ContextCard>

          <Separator height={16} transparent />
          {renderBody()}
        </ModalDescription>
      </ModalContent>
      <ModalActions>
        <Button color="black" onClick={closeModal}>
          Close
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default SlotDetail;
